import { Icon } from '@/components/ds'
import { cn } from '@/lib/utils'

export interface PasosAccesoProps {
  /** Etiquetas en orden. El largo define cuántos pasos hay. */
  pasos: string[]
  /** Paso actual, 1-indexado. */
  actual: number
  /**
   * Fuerza el modo compacto —sólo la etiqueta del paso actual— en TODO ancho.
   *
   * Hace falta cuando las etiquetas no caben ni con la tarjeta a su ancho
   * máximo: cuatro de ellas no entran en 440px. Con tres sí entran, así que la
   * recuperación no lo necesita y muestra las tres desde `sm`.
   */
  compacto?: boolean
}

/**
 * Riel de pasos de la recuperación de contraseña.
 *
 * No es el `Stepper` del DS: ese es el contador +/- para cantidades. Este es un
 * indicador de progreso de un flujo de varias pantallas, y existe porque la
 * recuperación es el único lugar donde le pedimos a alguien tres cosas
 * seguidas sin que vea el final. Sin el riel, el paso 2 ("escribe tu correo
 * completo") se lee como un interrogatorio; con el riel, se lee como el
 * penúltimo trámite.
 *
 * GEOMETRÍA — por qué no hay `flex-1` ni `shrink-0` acá:
 *
 * La primera versión ponía `flex-1` en cada paso y `shrink-0` en el bloque
 * círculo+etiqueta. `flex-1` es `flex: 1 1 0%`: reparte el ancho en partes
 * IGUALES ignorando que "Enlace enviado" mide el doble que "Tu RUT". Al bloque
 * interno, que no podía encogerse, no le quedaba más que desbordar su celda —
 * y como el `<ol>` no genera scroll, el desborde no se veía como desborde
 * sino como texto encimado con el círculo siguiente.
 *
 * Eso lo hacía además invisible para el chequeo habitual
 * (`scrollWidth > clientWidth` da falso: el riel mide igual, lo que se pisa es
 * el contenido). Ahora cada paso arranca del ancho de su contenido (`grow`,
 * basis auto) y puede encogerse (`min-w-0` + `truncate`): si alguna vez falta
 * espacio, la etiqueta se corta con elipsis en vez de montarse encima.
 *
 * VISIBILIDAD DE LAS ETIQUETAS — la tarjeta llega a su ancho máximo (440px)
 * recién a partir de ~472px de viewport, así que por debajo de `sm` el riel
 * siempre queda sin holgura: ahí muestra sólo la etiqueta del paso actual. El
 * truncado ("Tu R…", "Tu corr…") comunica menos que un círculo limpio. Desde
 * `sm` las tres de la recuperación entran completas.
 *
 * El texto no se pierde para lectores de pantalla: cada `<li>` lo lleva en su
 * `aria-label`, así que se anuncia igual esté visible o no.
 */
export function PasosAcceso({ pasos, actual, compacto = false }: PasosAccesoProps) {
  return (
    <ol className="flex items-center px-[26px] pt-[22px]">
      {pasos.map((label, i) => {
        const n = i + 1
        const hecho = n < actual
        const activo = n === actual
        const ultimo = n === pasos.length
        // El activo siempre se lee. Los demás: nunca en compacto, y desde `sm`
        // en el riel normal.
        const visibilidad = activo ? undefined : compacto ? 'hidden' : 'hidden sm:block'

        return (
          <li
            key={label}
            aria-current={activo ? 'step' : undefined}
            aria-label={hecho ? `${label}, completado` : label}
            className={cn('flex min-w-0 items-center gap-[9px]', !ultimo && 'grow')}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full text-[11.5px] font-semibold',
                  hecho
                    ? 'bg-primary text-primary-foreground'
                    : activo
                      ? 'bg-primary-100 text-primary-hover'
                      : 'bg-muted text-soft'
                )}
              >
                {hecho ? <Icon name="check" size={15} /> : n}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'truncate text-[11.5px]',
                  visibilidad,
                  activo
                    ? 'text-foreground font-semibold'
                    : hecho
                      ? 'text-body font-medium'
                      : 'text-soft font-medium'
                )}
              >
                {label}
              </span>
            </div>
            {!ultimo && (
              <div
                aria-hidden="true"
                className={cn('h-px min-w-3 flex-1', hecho ? 'bg-primary-border' : 'bg-border')}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}
